/**
 * Hala Control Center - Enterprise Admin Layout
 * Premium admin experience for Hala platform management
 */

import { useState, useEffect } from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  FolderKanban, 
  GitBranch, 
  MessageSquare, 
  Users, 
  AlertTriangle, 
  BarChart3, 
  Shield,
  Menu,
  X,
  Bell,
  Search,
  ChevronDown,
  ChevronLeft,
  LogOut,
  Settings,
  Package,
  UserCheck,
  GraduationCap,
  Calendar,
  Briefcase,
  Globe,
  Building,
  MapPin,
  Layers,
  Target,
  Play,
  List,
  Brain
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/control-center`;

// Design tokens
const COLORS = {
  primary: '#1B3A6B',
  primaryLight: '#2A4A7A',
  primaryDark: '#0F2341',
  gold: '#C5A572',
  goldLight: '#D4B95F',
  success: '#1D8F4E',
  warning: '#F59E0B',
  error: '#EF4444',
  white: '#FFFFFF',
  bg: '#F8FAFC',
  bgDark: '#F1F5F9',
  border: '#E2E8F0',
  text: '#1E293B',
  textMuted: '#64748B',
  textLight: '#94A3B8',
};

// Organized Navigation Structure
const NAV_SECTIONS = [
  {
    id: 'dashboard',
    items: [
      { path: '/admin', icon: LayoutDashboard, label: 'لوحة المعلومات' },
    ]
  },
  {
    id: 'crm',
    title: 'إدارة النظام',
    collapsible: true,
    items: [
      { path: '/admin/crm/users', icon: Users, label: 'المستخدمون' },
      { path: '/admin/crm/groups', icon: Layers, label: 'المجموعات' },
      { path: '/admin/crm/roles', icon: Shield, label: 'الأدوار والصلاحيات' },
    ]
  },
  {
    id: 'catalog',
    title: 'كتالوج الخدمات',
    items: [
      { path: '/admin/catalog', icon: FolderKanban, label: 'إدارة الخدمات' },
    ]
  },
  {
    id: 'resources',
    title: 'عناصر الخدمة',
    collapsible: true,
    items: [
      { path: '/admin/consultants', icon: UserCheck, label: 'المستشارون' },
      { path: '/admin/training-programs', icon: GraduationCap, label: 'البرامج التدريبية' },
      { path: '/admin/events', icon: Calendar, label: 'الفعاليات' },
      { path: '/admin/workspaces', icon: Briefcase, label: 'مساحات وباقات العمل' },
      { path: '/admin/digital-services', icon: Globe, label: 'الخدمات الرقمية' },
      { path: '/admin/branch-services', icon: Building, label: 'خدمات الفروع' },
    ]
  },
  {
    id: 'locations',
    title: 'المواقع والقنوات',
    collapsible: true,
    items: [
      { path: '/admin/cities', icon: MapPin, label: 'المدن' },
      { path: '/admin/branches', icon: Building, label: 'الفروع' },
    ]
  },
  {
    id: 'recommendations',
    title: 'محرك التوصيات',
    collapsible: true,
    items: [
      { path: '/admin/recommendations', icon: GitBranch, label: 'القواعد والمحاكي' },
    ]
  },
  {
    id: 'operations',
    title: 'العمليات',
    items: [
      { path: '/admin/sessions', icon: MessageSquare, label: 'الجلسات' },
      { path: '/admin/learning-center', icon: Brain, label: 'مركز التعلم' },
      { path: '/admin/ai-analytics', icon: BarChart3, label: 'تحليلات AI' },
    ]
  },
  {
    id: 'financial',
    title: 'المستشار المالي',
    items: [
      { path: '/admin/financial-settings', icon: Target, label: 'إعدادات التحليل المالي' },
    ]
  },
];

// Auth helper
const getAuthHeaders = () => {
  const token = localStorage.getItem('hala_admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {}
};

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [showAlerts, setShowAlerts] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({});

  const toggleSection = (sectionId) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchAlerts();
    }
  }, [isAuthenticated]);

  const checkAuth = async () => {
    const token = localStorage.getItem('hala_admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    
    try {
      const res = await axios.get(`${API}/auth/me`, { headers: getAuthHeaders() });
      setUser(res.data);
      setIsAuthenticated(true);
    } catch (err) {
      localStorage.removeItem('hala_admin_token');
      localStorage.removeItem('hala_admin_user');
      localStorage.removeItem('hala_admin_role');
      navigate('/admin/login');
    }
  };

  const fetchAlerts = async () => {
    try {
      const res = await axios.get(`${API}/dashboard/alerts`, { headers: getAuthHeaders() });
      setAlerts(res.data || []);
    } catch (e) {
      console.error('Failed to fetch alerts:', e);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('hala_admin_token');
    localStorage.removeItem('hala_admin_user');
    localStorage.removeItem('hala_admin_role');
    navigate('/admin/login');
  };

  const isActive = (path) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(path);
  };

  if (!isAuthenticated) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: COLORS.bg 
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: `3px solid ${COLORS.border}`,
            borderTopColor: COLORS.primary,
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p style={{ color: COLORS.textMuted, fontSize: '14px' }}>جاري التحقق...</p>
          <style>{`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', backgroundColor: COLORS.bg, fontFamily: "'Tajawal', sans-serif" }} dir="rtl">
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 40 }}
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        style={{
          width: sidebarOpen ? '280px' : '80px',
          backgroundColor: COLORS.primary,
          transition: 'all 0.3s ease',
          display: 'flex',
          flexDirection: 'column',
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          zIndex: 50,
          transform: mobileMenuOpen ? 'translateX(0)' : undefined,
        }}
        className="sidebar"
      >
        {/* Logo */}
        <div style={{ 
          padding: '24px', 
          borderBottom: `1px solid rgba(255,255,255,0.1)`,
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            backgroundColor: COLORS.gold,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <span style={{ color: COLORS.primary, fontWeight: 'bold', fontSize: '20px' }}>هـ</span>
          </div>
          {sidebarOpen && (
            <div>
              <h1 style={{ color: COLORS.white, fontSize: '18px', fontWeight: 'bold', margin: 0 }}>مركز تحكم هلا</h1>
              <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', margin: 0 }}>Hala Control Center</p>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav style={{ flex: 1, padding: '16px 12px', overflowY: 'auto' }}>
          {NAV_SECTIONS.map((section) => (
            <div key={section.id} style={{ marginBottom: '16px' }}>
              {/* Section Title */}
              {section.title && sidebarOpen && (
                <div
                  onClick={() => section.collapsible && toggleSection(section.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 16px',
                    marginBottom: '4px',
                    cursor: section.collapsible ? 'pointer' : 'default',
                  }}
                >
                  <span style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    color: 'rgba(255,255,255,0.5)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}>
                    {section.title}
                  </span>
                  {section.collapsible && (
                    <ChevronDown
                      size={14}
                      color="rgba(255,255,255,0.5)"
                      style={{
                        transform: collapsedSections[section.id] ? 'rotate(-90deg)' : 'rotate(0)',
                        transition: 'transform 0.2s',
                      }}
                    />
                  )}
                </div>
              )}
              
              {/* Section Items */}
              {(!section.collapsible || !collapsedSections[section.id]) && section.items.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: sidebarOpen ? '10px 16px' : '10px 12px',
                      borderRadius: '10px',
                      marginBottom: '2px',
                      backgroundColor: active ? 'rgba(255,255,255,0.15)' : 'transparent',
                      color: active ? COLORS.white : 'rgba(255,255,255,0.7)',
                      textDecoration: 'none',
                      transition: 'all 0.2s ease',
                      marginRight: sidebarOpen && section.title ? '8px' : '0',
                    }}
                    onMouseEnter={(e) => {
                      if (!active) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
                    }}
                    onMouseLeave={(e) => {
                      if (!active) e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <Icon size={18} />
                    {sidebarOpen && (
                      <span style={{ fontSize: '13px', fontWeight: active ? 600 : 400 }}>{item.label}</span>
                    )}
                    {active && sidebarOpen && (
                      <div style={{
                        marginRight: 'auto',
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        backgroundColor: COLORS.gold
                      }} />
                    )}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Sidebar Toggle */}
        <div style={{ padding: '16px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: 'rgba(255,255,255,0.1)',
              border: 'none',
              borderRadius: '8px',
              color: 'rgba(255,255,255,0.7)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            <Menu size={18} />
            {sidebarOpen && <span style={{ fontSize: '13px' }}>تصغير القائمة</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main style={{ 
        flex: 1, 
        marginRight: sidebarOpen ? '280px' : '80px',
        transition: 'margin 0.3s ease',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh'
      }}>
        {/* Top Header */}
        <header style={{
          backgroundColor: COLORS.white,
          borderBottom: `1px solid ${COLORS.border}`,
          padding: '16px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          zIndex: 30
        }}>
          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(true)}
            style={{
              display: 'none',
              padding: '8px',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer'
            }}
            className="mobile-menu-btn"
          >
            <Menu size={24} color={COLORS.text} />
          </button>

          {/* Search */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            backgroundColor: COLORS.bg,
            padding: '10px 16px',
            borderRadius: '10px',
            width: '400px',
            maxWidth: '100%'
          }}>
            <Search size={18} color={COLORS.textMuted} />
            <input
              type="text"
              placeholder="بحث في النظام..."
              style={{
                border: 'none',
                backgroundColor: 'transparent',
                outline: 'none',
                flex: 1,
                fontSize: '14px',
                color: COLORS.text,
                fontFamily: 'inherit'
              }}
            />
          </div>

          {/* Right Actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {/* Notifications */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setShowAlerts(!showAlerts)}
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  backgroundColor: COLORS.bg,
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative'
                }}
              >
                <Bell size={18} color={COLORS.textMuted} />
                {alerts.length > 0 && (
                  <span style={{
                    position: 'absolute',
                    top: '6px',
                    left: '6px',
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: COLORS.error
                  }} />
                )}
              </button>
              
              {/* Alerts Dropdown */}
              {showAlerts && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  marginTop: '8px',
                  width: '320px',
                  backgroundColor: COLORS.white,
                  borderRadius: '12px',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                  border: `1px solid ${COLORS.border}`,
                  overflow: 'hidden',
                  zIndex: 100
                }}>
                  <div style={{ padding: '16px', borderBottom: `1px solid ${COLORS.border}` }}>
                    <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: COLORS.text }}>التنبيهات</h3>
                  </div>
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {alerts.length === 0 ? (
                      <div style={{ padding: '24px', textAlign: 'center', color: COLORS.textMuted, fontSize: '13px' }}>
                        لا توجد تنبيهات
                      </div>
                    ) : (
                      alerts.map((alert, i) => (
                        <Link
                          key={i}
                          to={alert.action}
                          style={{
                            display: 'block',
                            padding: '12px 16px',
                            borderBottom: `1px solid ${COLORS.border}`,
                            textDecoration: 'none',
                            transition: 'background 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = COLORS.bg}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          onClick={() => setShowAlerts(false)}
                        >
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                            <div style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              marginTop: '6px',
                              backgroundColor: alert.type === 'error' ? COLORS.error : alert.type === 'warning' ? COLORS.warning : COLORS.primary
                            }} />
                            <div>
                              <p style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: COLORS.text }}>{alert.title}</p>
                              <p style={{ margin: '4px 0 0', fontSize: '12px', color: COLORS.textMuted }}>{alert.message}</p>
                            </div>
                          </div>
                        </Link>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* User Menu */}
            <div style={{ position: 'relative' }}>
              <div 
                onClick={() => setShowUserMenu(!showUserMenu)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '8px 12px',
                  backgroundColor: COLORS.bg,
                  borderRadius: '10px',
                  cursor: 'pointer'
                }}
              >
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '8px',
                  backgroundColor: COLORS.primary,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <span style={{ color: COLORS.white, fontWeight: 600, fontSize: '14px' }}>م</span>
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: COLORS.text }}>{user?.name_ar || 'مدير النظام'}</p>
                  <p style={{ margin: 0, fontSize: '11px', color: COLORS.textMuted }}>{user?.role || 'Admin'}</p>
                </div>
                <ChevronDown size={16} color={COLORS.textMuted} />
              </div>
              
              {/* User Dropdown Menu */}
              {showUserMenu && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  marginTop: '8px',
                  width: '200px',
                  backgroundColor: COLORS.white,
                  borderRadius: '12px',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                  border: `1px solid ${COLORS.border}`,
                  overflow: 'hidden',
                  zIndex: 100
                }}>
                  <button
                    onClick={handleLogout}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '14px 16px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      color: COLORS.error,
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      textAlign: 'right'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#FEF2F2'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <LogOut size={18} />
                    <span>تسجيل الخروج</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div style={{ flex: 1, padding: '24px', overflowY: 'auto' }}>
          <Outlet />
        </div>
      </main>

      <style>{`
        @media (max-width: 1024px) {
          .sidebar {
            transform: translateX(100%);
          }
          .sidebar.open {
            transform: translateX(0);
          }
          .mobile-menu-btn {
            display: flex !important;
          }
        }
      `}</style>
    </div>
  );
}
